import { useIntl } from '@/intl'
import { useAnalytics } from '@/scopes/common/infrastructure/analytics/hooks'
import { useFeatureFlag } from '@/scopes/common/infrastructure/feature-flags'
import { createSearchFilterClickEvent } from '@/scopes/search/search/domain/analytics'
import { useAiFilter } from '@/scopes/search/search/domain/hooks'
import FilterGroup from '@/scopes/search/search/pages/SearchPage/components/Filters/FilterGroup'
import { BaseInput, Button, Icon, SpaceBetween, tk, Typography } from '@sunwebgroup/ui-components'
import * as React from 'react'
import { css, styled } from 'styled-components'

const MAX_LENGTH = 250

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
`

const Form = styled.form`
  display: flex;
  flex-direction: column;
`

const TextArea = styled(BaseInput.Box)<{ $disabled: boolean }>`
  resize: none;
  width: 100%;
  height: 64px;
  padding: ${tk('spacing_sm')};
  box-sizing: border-box;
  cursor: text;
  border-radius: 6px;
  box-shadow: unset;
  border: 2px solid #9d8e7a;
  line-height: 18px;
  ${({ $disabled }) =>
    $disabled
    && css`
      color: ${tk('color_semantic_text_disabled')};
      border-color: ${tk('color_semantic_border_disabled')};
      background: ${tk('color_semantic_bg_disabled')};
      pointer-events: none;
    `}
`

const Label = styled(Typography)`
  margin-bottom: 6px;
  position: relative;
  top: -2px;
  display: flex;
  align-items: center;
`

const ActionButton = styled(Button)`
  min-height: 32px;
  height: 32px;
  margin: ${tk('spacing_xs')} 0 0 0 !important;
  position: relative;
`

const SparklesIcon = styled(Icon)`
  position: relative;
  top: 2px;
  margin: 0 0 0 ${tk('spacing_xs')};
  display: flex;
  align-items: center;
  svg {
    width: 20px;
    height: 20px;
  }
`

export function IntelligentFilter() {
  const intl = useIntl()
  const { setUserQuery, isFetching, isAIError, userQuery } = useAiFilter()
  const { sendEvent } = useAnalytics()
  const [value, setValue] = React.useState(userQuery ?? '')

  const showAIFilter = useFeatureFlag('ENABLE_AI_FILTER')

  React.useEffect(() => {
    setValue(userQuery ?? '')
  }, [userQuery])

  const confirm = () => {
    setUserQuery(value)
    sendEvent(
      createSearchFilterClickEvent({
        element: 'textField',
        type: 'AI question',
        result: value,
      }),
    )
  }

  const changeHandler = ({ target: { value: newValue } }: React.ChangeEvent<HTMLTextAreaElement>) => {
    if (newValue.length <= MAX_LENGTH) {
      setValue(newValue)
    }
  }

  const submitHandler = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    confirm()
  }

  const keyPressedHandler = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      confirm()
    }
  }

  return (
    showAIFilter && (
      <FilterGroup
        iconElement={<SparklesIcon glyph="IconSparkles" size="sm" />}
        title={intl.formatMessage({ id: 'search_ai_filter_label' })}
        defaultOpen
      >
        <Wrapper>
          <Label $variant="label-thin">{intl.formatMessage({ id: 'search_ai_filter_hint' })}</Label>
          <Form onSubmit={submitHandler}>
            <SpaceBetween direction="y" spacing={tk('spacing_md')}>
              {isAIError && (
                <Typography $variant="body-sm">{intl.formatMessage({ id: 'search_ai_filter_error' })}</Typography>
              )}
              <BaseInput.Root>
                <TextArea
                  as="textarea"
                  value={value}
                  onChange={changeHandler}
                  placeholder={intl.formatMessage({ id: 'search_ai_filter_placeholder' })}
                  onKeyDown={keyPressedHandler}
                  $disabled={isFetching}
                />
              </BaseInput.Root>
              <ActionButton height={32} type="submit" $withoutColor $hierarchy="secondary" disabled={isFetching}>
                {intl.formatMessage({ id: 'search_ai_filter_submit_text' })}
              </ActionButton>
            </SpaceBetween>
          </Form>
        </Wrapper>
      </FilterGroup>
    )
  )
}
